import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Box, ButtonBase, TableCell, Typography } from '@mui/material';

import CustomCheckbox from '@/components/Checkbox';
import type { SymbolKey } from '@/entities/symbol';
import FactsheetDialogButton from '@/features/Factsheet/components/FactsheetDialogButton';
import type { FullScreenLegendSeries } from '../fullScreenChartTypes';
import {
  DETAIL_COLUMNS,
  getCellValue,
  getLegendSymbol,
  getSelectorLabel
} from './legend.constants';
import { StyledTableCell, StyledTableRow } from './legend.styled';

interface LegendTableRowProps {
  isSpotLocked: boolean;
  isSymbolSelected: (symbol: SymbolKey | undefined) => boolean;
  item: FullScreenLegendSeries;
  onLockedClick: (symbol: SymbolKey | undefined) => void;
  onSelectionChange: (symbol: SymbolKey | undefined, selected: boolean) => void;
  onToggleSeries: (seriesId: string) => void;
}

const checkboxCellSx = { pr: 1 };
const checkboxRowSx = { alignItems: 'center', display: 'flex', gap: 1 };
const checkboxSx = { p: 0 };
const detailCellSx = { pr: 5 };

const LegendTableRow = ({
  isSpotLocked,
  isSymbolSelected,
  item,
  onLockedClick,
  onSelectionChange,
  onToggleSeries
}: LegendTableRowProps) => {
  const symbol = getLegendSymbol(item);
  const selected = isSymbolSelected(symbol);

  return (
    <StyledTableRow key={item.id}>
      <StyledTableCell
        scope='row'
        sx={checkboxCellSx}
        width={70}
      >
        <Box sx={checkboxRowSx}>
          <CustomCheckbox
            checked={selected}
            disabled={!symbol}
            slotProps={{ input: { 'aria-label': getSelectorLabel(item.name, selected) } }}
            modeLocked={isSpotLocked}
            onLockedClick={() => {
              onLockedClick(symbol);
            }}
            size='small'
            sx={checkboxSx}
            onChange={() => {
              onSelectionChange(symbol, selected);
            }}
          />
          <ButtonBase
            aria-label={`${item.visible ? 'Hide' : 'Show'} ${item.name}`}
            onClick={() => {
              onToggleSeries(item.id);
            }}
          >
            {item.visible ? <Visibility fontSize='small' /> : <VisibilityOff fontSize='small' />}
          </ButtonBase>
          <div
            style={{
              width: 16,
              height: 2,
              flexShrink: 0,
              backgroundColor: item.color
            }}
          />
        </Box>
      </StyledTableCell>

      {DETAIL_COLUMNS.map(column => (
        <TableCell
          key={column.key}
          align={column.align}
          sx={detailCellSx}
          title={getCellValue(item, column.key)}
          width={column.width}
        >
          <Typography variant='body2'>{getCellValue(item, column.key)}</Typography>
        </TableCell>
      ))}

      <TableCell
        align='right'
        width={60}
      >
        {item.code ? <FactsheetDialogButton code={item.code} /> : '-'}
      </TableCell>
    </StyledTableRow>
  );
};

export default LegendTableRow;
